import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    FormControlLabel,
    Checkbox,
    Alert,
    Link,
} from '@mui/material';
import { consentDocuments } from '../../utils/dropOptions';

function ConsentForms({ onNext }) {
    const [consents, setConsents] = useState({});
    const [error, setError] = useState(null);

    useEffect(() => {
        // Initialize consents state with all documents set to false
        const initialConsents = consentDocuments.reduce((acc, doc) => {
            acc[doc] = false;
            return acc;
        }, {});
        setConsents(initialConsents);
    }, []);

    const handleConsentChange = (document) => (event) => {
        setConsents({
            ...consents,
            [document]: event.target.checked
        });
    };

    const handleSubmit = () => {
        // Check if all documents are consented to
        const allConsented = Object.values(consents).every(value => value === true);
        if (!allConsented) {
            setError('Please acknowledge all documents before proceeding');
            return;
        }
        onNext(consents);
    };

    const formatDocumentFilename = (doc) => {
        return doc
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-') // Replace special chars with hyphens
            .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
    };

    const getDocumentPath = (doc) => {
        return `/assets/legal_paperwork/dynamic-freight-haulers/${formatDocumentFilename(doc)}.pdf`;
    };

    const allConsentsChecked = () => {
        return Object.values(consents).every(value => value === true);
    };

    return (
        <Box sx={{ maxWidth: 900, mx: 'auto', p: 3 }}>
            <Typography variant="h6" gutterBottom>Consent Forms</Typography>

            <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                    {consentDocuments.map((document) => (
                        <Box key={document} sx={{ mb: 4 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={consents[document] || false}
                                        onChange={handleConsentChange(document)}
                                    />
                                }
                                label={
                                    <Box>
                                        <Typography variant="subtitle1" sx={{ mb: 1 }}>
                                            <Link
                                                href={getDocumentPath(document)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                underline="hover"
                                                color="primary"
                                            >
                                                {document}
                                            </Link>
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            By checking this box, I (a) acknowledge that I have read and understand the{' '}
                                            <strong>{document}</strong> and have been given the opportunity to copy/print
                                            the Summary of Rights and (b) agree to use an electronic signature to demonstrate
                                            my consent. An electronic signature is as legally binding as an ink signature.
                                        </Typography>
                                    </Box>
                                }
                            />
                        </Box>
                    ))}

                    {error && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {error}
                        </Alert>
                    )}
                </CardContent>
            </Card>

            <Box sx={{ mt: 3 }}>
                <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={handleSubmit}
                    disabled={!allConsentsChecked()}
                >
                    Continue
                </Button>
            </Box>
        </Box>
    );
}

export default ConsentForms;
